import { useMemo, useState } from 'react';

import { ApiError } from '../api/client';
import type { FeedSeverity } from '../api/types';
import { EmptyState } from '../components/EmptyState';
import { EventFeedCardRow } from '../components/EventFeedCardRow';
import { useEventFeed } from '../hooks/useEventFeed';

import '../components/LogsScreen.css';

/** «Лента событий» — Phase 6.9 C2 rework of the old raw Logs screen.
 *
 *  The raw events journal was technical noise (control health pings,
 *  per-job state_change). The Лента is a curated, whitelisted feed
 *  served pre-grouped by ``GET /api/v1/events/feed``. The operator
 *  sees only meaningful operational / investigatory events, gated by
 *  three severity toggles.
 *
 *  Spec §B.4 — default: 🔴 critical ON, 🟡 warning ON, 🟢 info OFF, so
 *  the operator sees only what matters; one click on «всё» turns the
 *  full picture on.
 */

const ALL_SEVERITIES: readonly FeedSeverity[] = [
  'critical',
  'warning',
  'info',
] as const;

const SEVERITY_META: Record<FeedSeverity, { dot: string; label: string }> = {
  critical: { dot: '🔴', label: 'критичные' },
  warning: { dot: '🟡', label: 'важные' },
  info: { dot: '🟢', label: 'фон' },
};

export function LogsScreen() {
  // Spec §B.4 — default visible set is critical + warning (info off).
  const [severity, setSeverity] = useState<FeedSeverity[]>([
    'critical',
    'warning',
  ]);

  const allOff = severity.length === 0;

  const filters = useMemo(
    () => ({
      // All three on == «no filter»: omit the param so the backend
      // returns everything. A strict subset is sent verbatim.
      severity: severity.length === 3 ? undefined : severity,
    }),
    [severity],
  );
  // When every toggle is off there is nothing to ask for — park the
  // query (no request) and show an explicit empty state.
  const query = useEventFeed(filters, { enabled: !allOff });

  const toggle = (s: FeedSeverity) => {
    setSeverity((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  };
  const showAll = () => setSeverity([...ALL_SEVERITIES]);

  const cards = query.data?.items ?? [];

  return (
    <>
      <div
        className="feed-filter"
        role="tablist"
        aria-label="Фильтр по важности"
      >
        {ALL_SEVERITIES.map((s) => {
          const on = severity.includes(s);
          return (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={on}
              className={`feed-sev-toggle ${on ? 'active' : ''}`}
              data-testid={`feed-sev-${s}`}
              onClick={() => toggle(s)}
            >
              {SEVERITY_META[s].dot} {SEVERITY_META[s].label}
            </button>
          );
        })}
        <button
          type="button"
          className="feed-sev-all"
          data-testid="feed-sev-all"
          onClick={showAll}
        >
          всё
        </button>
      </div>
      <div className="screen">
        {allOff && <EmptyState>все фильтры важности выключены</EmptyState>}
        {!allOff && query.isLoading && <EmptyState>загрузка…</EmptyState>}
        {!allOff && query.isError && (
          <EmptyState>
            {query.error instanceof ApiError
              ? `ошибка ${query.error.status}: ${query.error.message}`
              : 'не удалось загрузить ленту'}
          </EmptyState>
        )}
        {!allOff && !query.isLoading && !query.isError && cards.length === 0 && (
          <EmptyState>событий нет</EmptyState>
        )}
        {!allOff && cards.length > 0 && (
          <div className="feed-stream">
            {cards.map((c) => (
              <EventFeedCardRow key={c.ids.join('-')} card={c} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}
